import { useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const TESTIMONIALS = [
  {
    text: "Martin is an excellent coach and mentor. We have used his mentoring skills, experience and coaching to help our team grow agile competencies. We have conducted training and many individual coaching sessions for individual scrum masters and product owners. And I have to say, we have all been very happy with every single session. Martin is very empathetic, has great insights and helped us by proposing many improvements and delivered very important guidance. I can absolutely recommend Martin for any agile trainings or mentoring!",
    name: "Jaroslav Kadlec",
    role: "Software Manager",
    portrait:
      "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/2ffmrs1z_1592206644013.jpg",
  },
  {
    text: "Martin is an amazing coach, mentor and great co-worker that I enjoyed very much to work with. He is extremely experienced about Agile methodologies, super knowledgeable and passes his knowledge on generously. I and Martin worked together at Pitcher, where Martin helped Pitcher to transform Pitcher to work agile. As one can imagine, there were a lot of difficulties and challenges during transformation period such as organisational workflows, processes, technical difficulties and most importantly; behaviour change of the team. Martin has dealt with every single problem very professionally and helped the team in every aspect to enable us to work agile.",
    name: "Onur Elibol",
    role: "Frontend Engineering Lead",
    portrait:
      "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/56xgl27t_1520287219241.jpg",
  },
  {
    text: "Martin is a professional in every way with a lot of experience with all implementing, keeping and adapting agile. After some time it gets quite obvious that his professional role is also his passion. Not ending there, he's also capable of coaching various soft-skills as well as merely mentoring. He's been a real asset to our team.",
    name: "Michal Bláha",
    role: "Senior / Principal Engineer",
    portrait:
      "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/zcsb9ocj_1604035634313.jpg",
  },
  {
    text: "Martin is a true professional and a rare responsible person. I've been given the chance to work with him on a daily basis from the start of his career in Kentico. Martin has a very deep knowledge of various agile methodologies and has plenty of experience both with introducing agile in more traditional companies as well as with coaching already established agile teams to continually deliver the value for the customer. I wouldn't hesitate to call Martin a full-stack agile coach capable of bringing, explaining and successfully executing agile principles to any company no matter its size.",
    name: "Zbyšek Němec",
    role: "CTO",
    portrait:
      "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/8zu39wgp_1632229777086.jpg",
  },
  {
    text: "Half a year mentoring with Martin on agile, SCRUM helped me a lot. Martin is very experienced and knows what he is talking about. He has not only theoretical considerations, but everything has been verified by practice. I had a lot of questions from my daily practice, to which I would have spent a really long time looking for an answer. Thanks to Martin, we did a really huge amount of work in a short time and I've moved a lot. I like how Martin thinks about things, we have a similar point of view. I like his style of working with people. He was always able to give good advice or explain the topic in such a way that it made sense not only to me, but also so that I was able to pass on the idea to the team.",
    name: "Irena Jarčíková",
    role: "Project Manager",
    portrait:
      "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/d4onnhh9_1658951311191.jpg",
  },
];

export const Reviews = () => {
  const { t } = useLanguage();
  const [idx, setIdx] = useState(0);
  const item = TESTIMONIALS[idx];
  const total = TESTIMONIALS.length;

  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="bg-[#F3F4F5] text-[#181B24] py-16 md:py-20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="font-body italic text-[#181B24]/70 text-base mb-3">
              {t.reviews.eyebrow}
            </p>
            <h2
              data-testid="reviews-title"
              className="font-heading uppercase font-bold leading-[1.05] tracking-[0.01em] text-4xl sm:text-5xl md:text-6xl"
            >
              {t.reviews.titleMain}{" "}
              <span className="gradient-text">{t.reviews.titleAccent}</span>
            </h2>
          </div>

          <div className="flex items-center gap-4">
            <div
              data-testid="reviews-counter"
              className="font-heading text-2xl font-semibold text-[#181B24]/80"
            >
              <span className="text-[#181B24]">{idx + 1}</span>
              <span className="mx-1 text-[#181B24]/30">/</span>
              <span>{total}</span>
            </div>
            <button
              data-testid="reviews-prev"
              onClick={prev}
              aria-label="Previous testimonial"
              className="w-12 h-12 rounded-full border-2 border-[#181B24] flex items-center justify-center hover:bg-[#181B24] hover:text-white transition-colors"
            >
              <ChevronLeft size={20} strokeWidth={2.5} />
            </button>
            <button
              data-testid="reviews-next"
              onClick={next}
              aria-label="Next testimonial"
              className="w-12 h-12 rounded-full bg-[#181B24] text-white flex items-center justify-center hover:bg-[#FFE21A] hover:text-[#181B24] transition-colors"
            >
              <ChevronRight size={20} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div
          data-testid="testimonial-card"
          className="relative bg-white rounded-[2rem] p-8 md:p-12 lg:p-16 shadow-[0_30px_60px_rgba(32,37,53,0.08)]"
        >
          <Quote
            size={64}
            className="absolute top-6 right-6 md:top-10 md:right-10 text-[#FFE21A] opacity-90"
            strokeWidth={1.5}
          />

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
            <div className="md:col-span-3 flex md:block items-center gap-4">
              <img
                src={item.portrait}
                alt={item.name}
                className="w-24 h-24 md:w-44 md:h-44 rounded-full object-cover ring-4 ring-[#FFE21A]/30"
                style={{ objectPosition: "center 25%" }}
                loading="lazy"
                data-testid="testimonial-portrait"
              />
              <div className="md:mt-5">
                <div
                  className="font-heading font-bold text-[#181B24] text-lg md:text-xl"
                  data-testid="testimonial-name"
                >
                  {item.name}
                </div>
                <div
                  className="font-body text-[#181B24]/60 text-sm"
                  data-testid="testimonial-role"
                >
                  {item.role}
                </div>
              </div>
            </div>

            <div className="md:col-span-9">
              <p
                key={idx}
                data-testid="testimonial-text"
                className="font-body text-[#181B24]/85 text-base md:text-lg leading-relaxed animate-fade-in-up"
              >
                &ldquo;{item.text}&rdquo;
              </p>
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="mt-8 flex items-center justify-center gap-2">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              data-testid={`reviews-dot-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                i === idx ? "w-10 bg-[#181B24]" : "w-2 bg-[#181B24]/30"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
